import { DownloadOutlined } from '@mui/icons-material'
import { Box, Grid, IconButton, Tooltip, Typography } from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined'
import { ApiGetDashboardData, ApiGetUsersExcel } from '../../apiService/Core/Apicall'
import PopupDialog from '../../Components/Popups/ConfirmDialog'
import DateRangeFilter from '../../Components/Common/DateRangeFilter'
import { useDateRange } from '../../hooks/useDateRange'
import LaunchOutlinedIcon from '@mui/icons-material/LaunchOutlined';
import { useNavigate } from 'react-router-dom'

const cards = [
  { title: 'Active Accounts', color: '#10B981', key: 'activeUsers' },
  { title: 'Inactive Accounts', color: '#F43F5E', key: 'inactiveUsers' },
  { title: 'Total Accounts', color: '#6366F1', key: 'totalUsers' },
  { title: 'Trading Accounts', color: '#0EA5E9', key: 'tradingUsers' },
  { title: 'Pending Trading Accounts', color: '#F59E0B', key: 'pendingTradingUsers' },
  { title: 'Rejected Trading Accounts', color: '#A855F7', key: 'rejectedTradingUsers' },
  { title: 'Monthly Active Accounts', color: '#14B8A6', key: 'activeMonthlyUsers' },
  { title: 'Weekly Active Accounts', color: '#EC4899', key: 'activeWeeklyUsers' },
  { title: 'Boosterloan Accounts', color: '#F97316', key: 'boosterLoanUsers' },
  { title: 'Funded Accounts', color: '#c4c4c4', key: 'fundedUsers' },
]

const CARD_TO_USER_FILTER: Record<string, string> = {
  activeUsers: 'Active',
  inactiveUsers: 'Inactive',
  totalUsers: 'Total',
  tradingUsers: 'Trading',
  pendingTradingUsers: 'Pending Trading',
  rejectedTradingUsers: 'Rejected',
  activeMonthlyUsers: 'Active Monthly',
  activeWeeklyUsers: 'Active Weekly',
  boosterLoanUsers: 'Booster Loan',
  fundedUsers: 'Funded',
}

const Dashboard: React.FC = () => {
  const [dashboardData, setDashboardData] = useState<any>({})
  const {
    range, customStart, setCustomStart, customEnd, setCustomEnd, handleRangeChange, buildQuery,
  } = useDateRange('dashboard', 'all')
  const [errorPopup, setErrorPopup] = useState<{ open: boolean; message: string }>({ open: false, message: '' })
  const navigate = useNavigate()

  const fetchDashboardData = useCallback(async () => {
    const query = buildQuery()
    if (query === null) return
    try {
      const res = await ApiGetDashboardData(`?${query}`)
      if (res?.status) setDashboardData(res?.data)
    } catch (error: any) {
      console.error("Error Getting Dashboard Data:", error)
      setErrorPopup({
        open: true,
        message: error?.message || error?.response?.data?.message || error || 'Something went wrong. Please try again.',
      })
    }
  }, [buildQuery])

  useEffect(() => { fetchDashboardData() }, [fetchDashboardData])

  const handleViewUsers = (key: string) => {
    navigate('/admin/users', {
      state: {
        organization: CARD_TO_USER_FILTER[key] || 'Total',
        range,
        customStart: customStart?.toISOString(),
        customEnd: customEnd?.toISOString(),
        backButton: true,
      },
    })
  }

  const handleDownloadUsersExcel = async (type: any) => {
    try {
      const query = buildQuery()
      const res = await ApiGetUsersExcel(`?type=${type}&${query}`)
      const blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `${type}.xlsx`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (error: any) {
      console.error("Error Downloading Excel.", error)
      setErrorPopup({
        open: true,
        message: error?.message || error?.response?.data?.message || error || 'Something went wrong. Please try again.',
      })
    }
  }

  return (
    <div className='common-layout'>
      <Grid container className="p-3 w-100" spacing={2}>
        <Grid size={{ xs: 12 }}>
          <DateRangeFilter
            range={range}
            onRangeChange={handleRangeChange}
            customStart={customStart}
            onCustomStartChange={setCustomStart}
            customEnd={customEnd}
            onCustomEndChange={setCustomEnd}
          />
        </Grid>

        {cards.map((item) => (
          <Grid key={item.key} size={{ xl: 4, md: 6, xs: 12 }}>
            <Box className="common-alignment bg-dark-color p-3 h-100 justify-content-center"
              sx={{
                border: `2px solid ${item.color}55`,
                borderRadius: "10px",
                position: 'relative',
                transition: 'all 0.3s ease',
                '&:hover': { transform: 'translateY(-6px) scale(1.03)', boxShadow: `0 10px 30px ${item.color}33` }
              }}
            >
              <IconButton
                onClick={() => handleDownloadUsersExcel(item?.key)}
                sx={{ position: 'absolute', top: 8, right: 8, color: item.color, transition: 'transform 0.3s ease', '&:hover': { transform: 'scale(1.2)' } }}
              >
                <Tooltip title="Download Excel Of This Users" arrow>
                  <DownloadOutlined />
                </Tooltip>
              </IconButton>
              <IconButton
                onClick={() => handleViewUsers(item.key)}
                sx={{ position: 'absolute', top: 8, right: 44, color: item.color, transition: 'transform 0.3s ease', '&:hover': { transform: 'scale(1.2)' } }}
              >
                <Tooltip title="View These Users" arrow>
                  <LaunchOutlinedIcon />
                </Tooltip>
              </IconButton>
              <Box className='common-alignment flex-column gap-1'>
                <PeopleAltOutlinedIcon sx={{ fontSize: 50, color: item.color, marginBottom: 1 }} />
                <Typography variant='h3' sx={{ color: item.color, fontWeight: 700 }}>
                  {dashboardData?.[item.key] ?? 0}
                </Typography>
                <Typography variant='subtitle1' sx={{ color: '#898989', fontWeight: 500 }}>
                  {item.title}
                </Typography>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>

      <PopupDialog
        open={errorPopup.open}
        onClose={() => setErrorPopup({ open: false, message: '' })}
        onConfirm={() => setErrorPopup({ open: false, message: '' })}
        variant="error"
        title="Something Went Wrong"
        message={errorPopup.message}
        confirmText="Try Again"
        cancelText="Dismiss"
      />
    </div>
  )
}

export default Dashboard